import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Header } from '../../components/layout';
import { Card, Loading } from '../../components/common';
import { StatsCard } from '../../components/modules/overview';
import { fetchStats } from '../../store/slices/statsSlice';
import { fetchAuthRisk, fetchAttackVectors, fetchMfaActivity } from '../../store/slices/dashboardSlice';
import AttackVectorModal from '../../components/modules/overview/AttackVectorModal/AttackVectorModal';
import AuthRiskVectorModal from '../../components/modules/overview/AuthRiskVectorModal/AuthRiskVectorModal';
import CriticalRiskIdentitiesTable from '../../components/modules/overview/CriticalRiskIdentitiesTable/CriticalRiskIdentitiesTable';
import TopAttackVectorsCard from '../../components/modules/overview/TopAttackVectorsCard/TopAttackVectorsCard';
import MfaActivityGraph from '../../components/modules/overview/MfaActivityGraph/MfaActivityGraph';
import OverviewService from '../../services/OverviewService';
import formatNumber from '../../utils/formatNumber';
import { fetchIdentities, fetchIdentityRemediation, clearCurrentRemediation } from '../../store/slices/identitiesSlice';
import IdentityDetail from '../../components/modules/identities/IdentityDetail/IdentityDetail';
import DomainRegistrationCard from '../../components/modules/overview/DomainRegistrationCard/DomainRegistrationCard';
import {
  FaUsers,
  FaUser,
  FaRobot,
  FaExclamationTriangle,
  FaShieldAlt
} from 'react-icons/fa';
import { AuthEventsPage } from "../AuthEvents/AuthEventsPage"
import './Overview.css';

const Overview = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState('human');
  const [identitiesTab, setIdentitiesTab] = useState('non-human');
  const [mfaTimeframe, setMfaTimeframe] = useState('7d');
  const [successRate, setSuccessRate] = useState(0);

  // Redux selectors
  const { data: stats, loading: statsLoading } = useSelector((state) => state?.stats);
  const { authRisk, attackVectors, mfaActivity } = useSelector((state) => state?.dashboard);

  const [selectedVector, setSelectedVector] = useState({ vector_id: null, totalIdentities: 0, attack_vector_name: "" });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSyncChecked, setIsSyncChecked] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncError, setSyncError] = useState(null);
  // New state for auth risk vector modal
  const [selectedAuthRiskVector, setSelectedAuthRiskVector] = useState(null);
  const [isAuthRiskModalOpen, setIsAuthRiskModalOpen] = useState(false);
  // Identity detail modal state (same as Identities page)
  const [selectedIdentity, setSelectedIdentity] = useState(null);
  const [isIdentityModalOpen, setIsIdentityModalOpen] = useState(false);
  const [selectedIdentityId, setSelectedIdentityId] = useState(null);

  const authRiskData = authRisk?.data;
  // console.log("vfvvf",authRiskData)
  const authRiskLoading = authRisk?.loading;
  const currentVectors = attackVectors[activeTab];
  const topAttackVectors = currentVectors?.data;
  const vectorsLoading = currentVectors?.loading;
  const { identities: criticalIdentities, pagination: critPagination, loading: criticalIdentitiesLoading } =
    useSelector((state) => state.identities);
  const { currentRemediation } = useSelector((state) => state.identities);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [identityCounts, setIdentityCounts] = useState({
    human: 0,
    'non-human': 0,
  });

  // Add useEffect to fetch and console
  useEffect(() => {
    if (!isSyncChecked || showSyncModal) return;
    dispatch(fetchMfaActivity(mfaTimeframe));
  }, [dispatch, mfaTimeframe, isSyncChecked, showSyncModal]);

  // Add this useEffect to console response when it loads
  useEffect(() => {
    if (mfaActivity?.data) {
      // console.log("MFA Activity Data:", mfaActivity?.data);
      // The breakdown is directly in mfaActivity.data, not mfaActivity.data.data
      const breakdown = mfaActivity?.data?.data?.breakdown;
      if (breakdown && breakdown.length > 0) {
        const legitimateData = breakdown.map(item => item.legitimate_count);
        const suspiciousData = breakdown.map(item => item.suspicious_count);
        // console.log("Legitimate Data:", legitimateData);
        // console.log("Suspicious Data:", suspiciousData);
        // Example: dates for x-axis
        const dates = breakdown.map(item => {
          const date = new Date(item.timestamp);
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        });
        // console.log("Dates:", dates);
      }
    }
    const totalLegitimate = mfaActivity?.data?.data?.legitimate_count;
    const totalSuspicious = mfaActivity?.data?.data?.suspicious_count;
    const rate = mfaActivity?.data?.data?.success_percentage;
    setSuccessRate(rate);
    // console.log("Total Legitimate:", totalLegitimate);
    // console.log("Total Suspicious:", totalSuspicious);
    // console.log("success Rate", successRate)
    if (mfaActivity?.error) {
      console.error("MFA Activity Error:", mfaActivity.error);
    }
  }, [mfaActivity]);

  useEffect(() => {
    if (!isSyncChecked || showSyncModal) return;
    dispatch(
      fetchIdentities({
        account_type: 'critical',
        page: 1,
        limit: 10,
        page: currentPage,
        limit: pageSize,
        sortBy: 'displayName',
        sortOrder: 'asc',
        search: '',
        type: identitiesTab,
      })
    );
  }, [dispatch, isSyncChecked, showSyncModal, identitiesTab, currentPage, pageSize]);

  useEffect(() => {
    const verifyDataSync = async () => {
      try {
        const response = await OverviewService.checkDataSync();
        if (response?.data?.exists === false) {
          setShowSyncModal(true);
        }
      } catch (error) {
        // console.log("Failed to check data sync:", error?.response?.data?.error);
        setSyncError(error?.response?.data?.error || "An unexpected error occurred while verifying connection status.");
        setShowSyncModal(true);
      } finally {
        setIsSyncChecked(true);
      }
    };
    verifyDataSync();
  }, []);

  // 2. Fetch Base Stats (Blocked if modal is open or sync unchecked)
  useEffect(() => {
    if (!isSyncChecked || showSyncModal) return;
    if (!stats?.total?.total_latest_identities) {
      dispatch(fetchStats());
    }
    if (!authRiskData) {
      dispatch(fetchAuthRisk());
    }
  }, [dispatch, stats?.total?.total_latest_identities, authRiskData, isSyncChecked, showSyncModal]);

  // 3. Fetch Attack Vectors (Blocked if modal is open or sync unchecked)
  useEffect(() => {
    if (!isSyncChecked || showSyncModal) return;
    dispatch(fetchAttackVectors(activeTab));
  }, [activeTab, dispatch, isSyncChecked, showSyncModal]);
  const primaryStats = [
    { title: 'Total Identities', value: formatNumber(stats?.total?.total_latest_identities ?? 0), icon: <FaUsers />, iconColor: 'primary', trend: stats?.total?.percentage_change },
    { title: 'Human Identities', value: formatNumber(stats?.human_identities?.latest_total ?? 0), icon: <FaUser />, iconColor: 'primary', trend: stats?.human_identities?.percentage_change },
    { title: 'Non-Human Identities', value: formatNumber(stats?.non_human_identities?.latest_total ?? 0), icon: <FaRobot />, iconColor: 'success', trend: stats?.non_human_identities?.percentage_change },
    { title: 'Global Posture Score', value: Math.round(stats?.risk_stats?.score?.latest_percentage ?? 0), icon: <FaShieldAlt />, iconColor: 'success', trend: stats?.risk_stats?.score?.percentage_change }
  ];
  const secondaryStats = [
    { title: 'Medium Risk', value: formatNumber(stats?.risk_stats?.medium?.latest_total ?? 0), icon: <FaShieldAlt />, iconColor: 'warning', trend: stats?.risk_stats?.medium?.percentage_change },
    { title: 'Dormant Accounts', value: formatNumber(stats?.risk_stats?.dormant_accounts?.latest_total ?? 0), icon: <FaShieldAlt />, iconColor: 'info', trend: stats?.risk_stats?.dormant_accounts?.percentage_change },
    { title: 'High Risk', value: formatNumber(stats?.risk_stats?.high?.latest_total ?? 0), icon: <FaExclamationTriangle />, iconColor: 'danger', trend: stats?.risk_stats?.high?.percentage_change },
    { title: 'MFA Not Registred', value: formatNumber(stats?.risk_stats?.mfa_not_registered?.latest_total ?? 0), icon: <FaExclamationTriangle />, iconColor: 'warning', trend: stats?.risk_stats?.mfa_not_registered?.percentage_change },
  ];

  const handleAuthRiskVectorClick = (vector) => {
    setSelectedAuthRiskVector(vector);
    setIsAuthRiskModalOpen(true);
  };

  // ── Identity Detail Handlers (same as Identities page) ──
  const openIdentityDetail = (identity) => {
    setSelectedIdentity(identity);
    setIsIdentityModalOpen(true);
    const id = identity?.id || identity?.objectId;
    if (id) {
      dispatch(fetchIdentityRemediation(id));
    }
  };

  const closeIdentityDetail = () => {
    setIsIdentityModalOpen(false);
    setSelectedIdentity(null);
    dispatch(clearCurrentRemediation());
  };

  // Handle row click from CriticalRiskIdentitiesTable
  const handleCriticalIdentityRowClick = (identity) => {
    openIdentityDetail(identity);
  };

  // Handle view details from CriticalRiskIdentitiesTable
  const handleCriticalIdentityViewDetails = (identity) => {
    openIdentityDetail(identity);
  };

  // Phase B: Intercept and show Modal (Handles both: Explicit "No Data Found" OR API connection error)
  if (showSyncModal) {
    return (
      <div className="sync-modal-overlay">
        <div className="sync-modal-content">
          {syncError ? (
            <>
              <h2 style={{ color: 'var(--danger, #dc3545)' }}>Connection Error</h2>
              <p>We encountered an issue checking your sync status: <strong>{syncError}</strong></p>
              <div className="sync-modal-actions">
                <button
                  className="sync-now-btn"
                  onClick={() => window.location.reload()}
                >
                  Retry Connection
                </button>
              </div>
            </>
          ) : (
            <>
              <h2>No Data Found</h2>
              <p>No previous data synchronization was found. Please sync your data first to view analytics.</p>
              <div className="sync-modal-actions">
                <button
                  className="sync-now-btn"
                  onClick={() => navigate('/integrations')}
                >
                  Go to Integrations & Sync
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="overview-page">
      <Header
        title="Overview"
        subtitle="Identity Security Posture Management Dashboard"
        showSearch={false}
        showSync={false}
      />
      <section className="overview-stats">
        {primaryStats.map((stat, index) => (
          <StatsCard key={index} {...stat} loading={statsLoading} />
        ))}
      </section>
      <section className="overview-stats secondary">
        {secondaryStats.map((stat, index) => (
          <StatsCard key={index} {...stat} loading={statsLoading} />
        ))}
      </section>
      <section className="overview-analysis-grid">
        <Card
          title={"Authentication Risk"}
          subtitle={"Suspicious authentication activity overview"}
        >
          {authRiskLoading && !authRiskData ? (
            <Loading text="Loading Authentication Risk" />
          ) : (
            <div className="auth-risk-wrapper">
              {/* Stats */}
              <div className="auth-risk-stats">
                <div className="auth-stat-card">
                  <div className="stat-content">
                    <span className="stat-label">Total Authentications</span>
                    <span className="stat-number">
                      {formatNumber(
                        authRiskData?.overview?.find(
                          (item) =>
                            item.metric_name === "total_authentications",
                        )?.value ?? 0,
                      )}
                    </span>
                  </div>
                </div>

                <div className="auth-stat-card risk">
                  <div className="stat-content">
                    <span className="stat-label">Risky Authentications</span>

                    <div className="risk-number-row">
                      <span className="stat-number">
                        {formatNumber(
                          authRiskData?.overview?.find(
                            (item) =>
                              item.metric_name === "risky_authentications",
                          )?.value ?? 0,
                        )}
                      </span>

                      <span className="risk-pill">
                        {authRiskData?.overview?.find(
                          (item) =>
                            item.metric_name === "risky_authentications",
                        )?.percentage ?? "0%"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Attacks */}
              <div className="risk-section">
                <div className="risk-section-header">
                  <h3>Top Attacks</h3>

                  <span>
                    {(authRiskData?.top_risk_vectors || []).length} detected
                  </span>
                </div>

                <div className="risk-list">
                  {(authRiskData?.top_risk_vectors || []).map((v, i) => (
                    <div
                      key={i}
                      className="risk-item"
                    // onClick={() => handleAuthRiskVectorClick(v)}
                    >
                      <div className="risk-item-left">
                        <span
                          className={`risk-dot ${v?.risk_level || "medium"}`}
                        />

                        <div className="risk-text-group">
                          <span className="risk-title">
                            {v?.metric_name || "-"}
                          </span>

                          <span className="risk-subtitle">
                            Risk Level: {v?.risk_level || "Unknown"}
                          </span>
                        </div>
                      </div>

                      <div className="risk-arrow">
                        {v?.value || 0}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Card>
        <Card
          className="attack-vectors-card"
          title={"Top 5 Attack Vectors"}
          subtitle={"Most targeted identity attack patterns"}
          headerActions={
            <div className="vector-tab-toggle">
              {["human", "non-human"].map((type) => (
                <button
                  key={type}
                  className={`vector-tab-btn ${activeTab === type ? "active" : ""}`}
                  onClick={() => setActiveTab(type)}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          }
        >
          <div className="attack-vector-wrapper">
            {vectorsLoading ? (
              <Loading text="Loading attack vectors..." />
            ) : topAttackVectors.length > 0 ? (
              <div className="attack-vector-list">
                {topAttackVectors.map((vector, i) => (
                  <div
                    key={i}
                    className="attack-vector-card"
                    onClick={() => {
                      setSelectedVector({
                        vector_id: vector?.vector_id,
                        totalIdentities: vector?.total_identities,
                        attack_vector_name: vector.attack_vector_name,
                      });
                      setIsModalOpen(true);
                    }}
                  >
                    {/* Left */}
                    <div className="vector-left">
                      <div className="vector-content">
                        <h4 className="vector-name">
                          {vector?.attack_vector_name?.replace(/_/g, " ") ||
                            "-"}
                        </h4>
                        <span className="vector-subtext">
                          Attack vector detected
                        </span>
                      </div>
                    </div>
                    {/* Right */}
                    <div className="vector-right">
                      <div className="vector-count">
                        {formatNumber(vector?.total_identities ?? 0)}
                      </div>
                      <span className="vector-label">identities</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="attack-empty-state">
                <p>No vectors found for this type.</p>
              </div>
            )}
            <button
              className="view-all-vectors-btn"
              onClick={() => navigate("/policies")}
            >
              View All {activeTab.replace("-", " ")} Vectors
            </button>
          </div>

        </Card>

        {/* </section> */}
      </section>
      <AuthEventsPage />

      {/* <TopAttackVectorsCard
        vectors={topAttackVectors || []}
        loading={vectorsLoading}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onViewAll={() => navigate('/policies')}
        onVectorClick={(vectorData) => {
          setSelectedVector({
            vector_id: vectorData.vector_id,
            totalIdentities: vectorData.totalIdentities,
            attack_vector_name: vectorData.attack_vector_name
          });
          setIsModalOpen(true);
        }}
      /> */}

      <MfaActivityGraph
        data={mfaActivity?.data?.data || mfaActivity?.data}
        timeframe={mfaTimeframe}
        onTimeframeChange={setMfaTimeframe}
        successRate={successRate}
        loading={mfaActivity?.loading}
      />
      {/* CriticalRiskIdentitiesTable with onRowClick and onViewDetails */}
      {stats?.risk_stats?.critical?.latest_total > 0 && (
        <CriticalRiskIdentitiesTable
          identities={criticalIdentities}
          loading={criticalIdentitiesLoading}
          activeTab={identitiesTab}
          pagination={critPagination}
          onTabChange={(tab) => {
            setIdentitiesTab(tab);
            setCurrentPage(1);
          }}
          onViewAllIdentities={() => navigate("/identities")}
          onPageChange={(page) => setCurrentPage(page)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          onRowClick={handleCriticalIdentityRowClick}
          onViewDetails={handleCriticalIdentityViewDetails}
          selectedId={selectedIdentityId}
        />
      )}

      {/* Attack Vector Modal */}
      <AttackVectorModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        vectorData={selectedVector}
        activeTab={activeTab}
      />
      {/* Auth Risk Vector Modal */}
      {/* <AuthRiskVectorModal
        isOpen={isAuthRiskModalOpen}
        onClose={() => setIsAuthRiskModalOpen(false)}
        vectorData={selectedAuthRiskVector}
      /> */}
      {/* Identity Detail Modal */}
      <IdentityDetail
        identity={selectedIdentity}
        isOpen={isIdentityModalOpen}
        onClose={closeIdentityDetail}
        remediation={currentRemediation.data}
        loading={currentRemediation.loading}
      />
      <DomainRegistrationCard showRefresh={false} />
      {showSyncModal && (
        <div className="sync-modal-overlay">
          <div className="sync-modal-content">
            <h2>No Data Found</h2>
            <p>
              No previous data synchronization was found. Please sync your data
              first to view analytics.
            </p>
            <div className="sync-modal-actions">
              <button
                className="sync-now-btn"
                onClick={() => navigate("/integrations")}
              >
                Go to Integrations & Sync
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default Overview;